import { useDndContext, useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, Dumbbell, GripVertical, Utensils } from 'lucide-react'

export const MealDropZone = ({ mealId, planId, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `meal-drop-${mealId}`, data: { type: 'mealDrop', mealId, planId } })
  const { active } = useDndContext()
  const activeType = active?.data?.current?.type
  // Only the drop targets handleNutritionDragEnd actually honors: a library item (always valid),
  // or a meal item being reordered within its own meal. Highlighting anything else would promise
  // a drop that silently does nothing.
  const isValidTarget =
    activeType === 'libraryItem' || (activeType === 'mealItem' && active?.data?.current?.mealId === mealId)
  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg transition-shadow duration-150 ${
        isOver && isValidTarget ? 'ring-2 ring-fitness-primary/50 ring-offset-2 ring-offset-slate-50' : ''
      }`}
    >
      {children}
    </div>
  )
}

export const LibraryLeaf = ({ node }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useDraggable({
    id: node.id,
    data: { type: 'libraryItem', node },
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`group flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm transition cursor-grab active:cursor-grabbing ${
        isDragging
          ? 'border-slate-200 bg-white/40 opacity-0'
          : 'border-slate-300 bg-white hover:border-fitness-primary/50 hover:bg-fitness-primary/[0.03] hover:shadow-sm'
      }`}
    >
      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-slate-50 text-slate-400 transition group-hover:bg-fitness-primary/10 group-hover:text-fitness-primary">
        <Utensils className="h-3.5 w-3.5" />
      </div>
      <span className="min-w-0 flex-1 text-sm font-medium leading-snug text-slate-800 break-words whitespace-normal">
        {node.name}
      </span>
      <GripVertical className="h-4 w-4 flex-shrink-0 text-slate-300 transition group-hover:text-fitness-primary" />
    </div>
  )
}

export const LibraryDragOverlay = ({ node }) => (
  <div className="pointer-events-none flex items-center gap-3 rounded-lg border border-fitness-primary/40 bg-white px-4 py-3 text-sm shadow-sm ring-2 ring-fitness-primary/30">
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-fitness-primary/10 text-fitness-primary">
      <Utensils className="h-4 w-4" />
    </div>
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-sm font-semibold leading-snug text-slate-800 break-words whitespace-normal">{node.name}</span>
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold">
        <span className="rounded-full bg-fitness-primary/10 px-1.5 py-0.5 text-fitness-primary">
          {node.calories ? `${node.calories} kcal` : '--'}
        </span>
        <span className="rounded-full bg-violet-50 px-1.5 py-0.5 text-violet-600">P {node.protein ?? 0}g</span>
        <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-amber-600">C {node.carbs ?? 0}g</span>
        <span className="rounded-full bg-sky-50 px-1.5 py-0.5 text-sky-600">F {node.fat ?? 0}g</span>
      </div>
    </div>
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-fitness-primary/40 bg-fitness-primary/10 text-fitness-primary">
      <GripVertical className="h-4 w-4" />
    </div>
  </div>
)

export const ExerciseDropZone = ({ planId, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `exercise-drop-${planId}`, data: { type: 'exerciseDrop', planId } })
  return (
    <div ref={setNodeRef} className={isOver ? 'ring-2 ring-fitness-primary/50 rounded-xl' : ''}>
      {children}
    </div>
  )
}

export const ExerciseLibraryLeaf = ({ node }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useDraggable({
    id: `exercise-${node.id}`,
    data: { type: 'exerciseLibraryItem', node },
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`group flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm transition cursor-grab active:cursor-grabbing ${
        isDragging
          ? 'border-slate-200 bg-white/40 opacity-0'
          : 'border-slate-300 bg-white hover:border-fitness-primary/50 hover:bg-fitness-primary/[0.03] hover:shadow-sm'
      }`}
    >
      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-slate-50 text-slate-400 transition group-hover:bg-fitness-primary/10 group-hover:text-fitness-primary">
        <Dumbbell className="h-3.5 w-3.5" />
      </div>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{node.name}</span>
      <GripVertical className="h-4 w-4 flex-shrink-0 text-slate-300 transition group-hover:text-fitness-primary" />
    </div>
  )
}

export const ExerciseLibraryDragOverlay = ({ node }) => (
  <div className="pointer-events-none flex items-center gap-3 rounded-lg border border-fitness-primary/40 bg-white px-4 py-3 text-sm shadow-sm ring-2 ring-fitness-primary/30">
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-fitness-primary/10 text-fitness-primary">
      <Dumbbell className="h-4 w-4" />
    </div>
    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{node.name}</span>
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-fitness-primary/40 bg-fitness-primary/10 text-fitness-primary">
      <GripVertical className="h-4 w-4" />
    </div>
  </div>
)

export const LibraryGroup = ({ node, expanded, onToggle, childrenCount, children, depth = 0 }) => {
  const isTopLevel = depth === 0
  return (
    <div className={isTopLevel ? 'pb-1 last:pb-0' : ''}>
      <button
        type="button"
        onClick={onToggle}
        className={`group flex w-full items-center justify-between gap-2 text-left transition ${
          isTopLevel
            ? `rounded-md border-l-[3px] px-2 py-1.5 text-[13px] font-bold uppercase tracking-wide transition-colors ${
                expanded
                  ? 'border-fitness-primary bg-fitness-primary/[0.06] text-slate-900'
                  : 'border-slate-300 text-slate-700 hover:border-fitness-primary/40 hover:bg-slate-50'
              }`
            : 'rounded-md px-1.5 py-1 text-sm font-medium text-slate-600 hover:bg-slate-50'
        }`}
      >
        <span className="flex min-w-0 items-center gap-2">
          <ChevronDown
            className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform duration-200 group-hover:text-slate-600 ${
              expanded ? '' : '-rotate-90'
            }`}
          />
          <span className="truncate">{node.name}</span>
        </span>
        <span
          className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            isTopLevel ? 'bg-fitness-primary/10 text-fitness-primary' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {childrenCount}
        </span>
      </button>
      {expanded && (
        <div className="ml-2 mt-0.5 space-y-1 border-l-2 border-slate-300 py-0.5 pl-3">{children}</div>
      )}
    </div>
  )
}
